import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MemberService } from '../../../services/member.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import {
  MemberGridResponse,
  MemberGridResponseData,
} from '../../../models/memberTable';
import { NzModalService } from 'ng-zorro-antd/modal';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';

import { PartnerGridRequest } from '../../../models/partnerGridRequest';
import { lastValueFrom } from 'rxjs';
import { PartnerService } from '../../../services/partner.service';
import { Router } from '@angular/router';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { Roles } from 'projects/app-core/src/auth/models/roles';

@Component({
  selector: 'app-member-table',
  templateUrl: './member-table.component.html',
  styleUrl: './member-table.component.scss',
})
export class MemberTableComponent {
  pageSize: number = 10;
  memberTableCurrentPageNo: number = 1;
  setOfCheckedId: Set<string> = new Set<string>();
  listOfCurrentPageData: readonly MemberGridResponseData[] = [];
  checked: boolean = false;
  @Input() selectedTab!: number;
  @Input() tableData!: MemberGridResponse;
  memberSizeOptions = [10, 20, 50, 100];
  @Input() partnerName = '';
  emailColumnSearch: string = '';
  corporateCodeColumnSearch: string = '';
  codeRedeemColumnValue: string | undefined;
  partnerList: {
    label: string;
    value: string;
  }[] = [];
  @Output() reloadTable = new EventEmitter<void>();
  selectedPartner: string | undefined;
  transferLoading: boolean = false;

  @Output() exportMemberTableEvent = new EventEmitter<void>();
  @Output() resetFilterEvent = new EventEmitter<void>();
  @Output() pageSizeChangeEvent = new EventEmitter<number>();
  @Output() pageChangeEvent = new EventEmitter<number>();
  @Output() deleteMemberEvent = new EventEmitter<string>();
  @Output() corporateSearchEvent = new EventEmitter<string>();
  @Output() emailSearchEvent = new EventEmitter<string>();
  @Output() codeReedemFilterEvent = new EventEmitter<string>();
  @Output() convertCorporateUserToRetailFilterEvent = new EventEmitter<string>();

  protected showTransferMemberModal: boolean = false;
  private selectedMemberId: string | undefined;
  protected showActivePlansWarningModal: boolean = false;
  protected activePlansCount: number = 0;
  protected activePacksCount: number = 0;
  protected pendingAction: 'transfer' | 'delete' | 'bulk-delete' | 'convert' | null = null;
  protected membersWithActivePlans: string[] = [];
  private selectedMemberEmail: string | undefined;

  constructor(
    private memberService: MemberService,
    private toastService: ToastService,
    private modal: NzModalService,
    private partnerService: PartnerService,
    private router: Router,
    private auth: AuthService,
  ) { }

  async convertCorporateToRetail(email: string, memberId: string) {
    this.selectedMemberEmail = email;
    this.selectedMemberId = memberId;

    try {
      // Check for active plans/packs first
      const { activePlans, activePacks } = await this.checkActivePlansAndPacks(memberId);

      if (activePlans > 0 || activePacks > 0) {
        this.activePlansCount = activePlans;
        this.activePacksCount = activePacks;
        this.pendingAction = 'convert';
        this.showActivePlansPacksWarning();
        return;
      }

      // No active plans/packs, proceed with generic confirm
      this.promptConvertConfirm();
    } catch (error) {
      this.toastService.showError('Failed to check active plans/packs. Please try again.');
      console.error('Error checking active plans/packs:', error);
    }
  }

  private promptConvertConfirm() {
    this.modal.confirm({
      nzTitle: 'Convert Corporate to Retail',
      nzContent: 'Are you sure you want to convert this corporate member to retail?',
      nzOnOk: async () => {
        this.proceedWithConvert();
      },
      nzOnCancel: () => {
        // Clean up on cancel
        this.selectedMemberEmail = undefined;
        this.selectedMemberId = undefined;
      },
      nzOkText: 'Convert',
      nzCancelText: 'Cancel',
      nzClosable: false,
    });
  }

  navigateToUserProfile(code: string) {
    window.open(`${'/user-profile/'}?code=${code}`);
  }

  resetValues() {
    this.emailColumnSearch = '';
    this.codeRedeemColumnValue = '';
  }

  exportMemberTable() {
    this.exportMemberTableEvent.emit();
  }
  resetFilter() {
    this.resetFilterEvent.emit();
  }
  nzPageIndexChange($event: number) {
    this.pageChangeEvent.emit($event);
  }
  nzPageSizeChange($event: number) {
    this.pageSizeChangeEvent.emit($event);
  }
  async deleteMemberAlert($event: string) {
    this.selectedMemberId = $event;

    try {
      // Check for active plans and packs
      const { activePlans, activePacks } = await this.checkActivePlansAndPacks($event);

      if (activePlans > 0 || activePacks > 0) {
        // Member has active plans/packs, show warning
        this.activePlansCount = activePlans;
        this.activePacksCount = activePacks;
        this.pendingAction = 'delete';
        this.showActivePlansPacksWarning();
        return;
      }

      // No active plans/packs, proceed with delete
      this.proceedWithDelete();
    } catch (error) {
      this.toastService.showError('Failed to check active plans/packs. Please try again.');
      console.error('Error checking active plans/packs:', error);
    }
  }
  corporateCodeSearchFn() {
    this.corporateSearchEvent.emit(this.corporateCodeColumnSearch.trim());
  }
  codeRedeemColumnFilterFn() {
    this.codeReedemFilterEvent.emit(this.codeRedeemColumnValue);
  }
  emailColumnSearchFn() {
    this.emailSearchEvent.emit(this.emailColumnSearch.trim());
  }

  async bulkDeleteMember() {
    if (this.setOfCheckedId.size == 0) {
      this.toastService.showError(
        'Please select atleast one member to delete',
      );
      return;
    }

    try {
      // Check for active plans/packs for all selected members
      const checkPromises = Array.from(this.setOfCheckedId).map(async (id) => {
        const result = await this.checkActivePlansAndPacks(id);
        return {
          id,
          hasActive: result.activePlans > 0 || result.activePacks > 0,
          activePlans: result.activePlans,
          activePacks: result.activePacks,
        };
      });

      const results = await Promise.all(checkPromises);
      const membersWithActive = results.filter(r => r.hasActive);

      if (membersWithActive.length > 0) {
        // Some members have active plans/packs
        const totalPlans = membersWithActive.reduce((sum, m) => sum + m.activePlans, 0);
        const totalPacks = membersWithActive.reduce((sum, m) => sum + m.activePacks, 0);

        this.activePlansCount = totalPlans;
        this.activePacksCount = totalPacks;
        this.pendingAction = 'bulk-delete';
        this.membersWithActivePlans = membersWithActive.map(m => m.id);
        this.showActivePlansPacksWarning();
        return;
      }

      // No active plans/packs, proceed with bulk delete
      this.proceedWithBulkDelete();
    } catch (error) {
      this.toastService.showError('Failed to check active plans/packs. Please try again.');
      console.error('Error checking active plans/packs:', error);
    }
  }

  bulkSendMail(): void {
    if (this.setOfCheckedId.size == 0) {
      this.toastService.showError(
        'Please select atleast one member to send mail',
      );
      return;
    }
    this.modal.confirm({
      nzTitle: 'Send Mail',
      nzContent: `Do you want to send mail to ${this.setOfCheckedId.size} members?`,
      nzOnOk: async () => {
        return new Promise((resolve, reject) => {
          this.memberService
            .bulkSendMemberPass(Array.from(this.setOfCheckedId))
            .subscribe({
              next: () => {
                this.toastService.showSuccess('Mails sent successfully');
                resolve();
              },
              error: (err) => {
                const errorBody = err.error as IAnyoError;
                this.toastService.showAnyoErrorToast(errorBody.description);
                reject();
              },
            });
        });
      },
      nzOnCancel: async () => { },
      nzOkText: 'Send',
      nzCancelText: 'Cancel',
      nzClosable: false,
    });
  }

  updateCheckedSet(id: string, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  onItemChecked(id: string, checked: boolean) {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus();
  }

  onAllChecked(checked: boolean) {
    this.listOfCurrentPageData.forEach(({ id }) =>
      this.updateCheckedSet(id, checked),
    );
    this.refreshCheckedStatus();
  }

  onCurrentPageDataChange(
    listOfCurrentPageData: readonly MemberGridResponseData[],
  ) {
    this.listOfCurrentPageData = listOfCurrentPageData;
    this.checked = this.listOfCurrentPageData.every(({ id }) =>
      this.setOfCheckedId.has(id),
    );
  }
  refreshCheckedStatus(): void {
    this.checked = this.listOfCurrentPageData.every(({ id }) =>
      this.setOfCheckedId.has(id),
    );
  }

  clearSelection(): void {
    this.setOfCheckedId.clear();
  }

  async showTransferPartnerFn(id: string) {
    this.selectedMemberId = id;

    try {
      const { activePlans, activePacks } = await this.checkActivePlansAndPacks(id);

      if (activePlans > 0 || activePacks > 0) {
        this.activePlansCount = activePlans;
        this.activePacksCount = activePacks;
        this.pendingAction = 'transfer';
        this.showActivePlansPacksWarning();
        return;
      }

      this.proceedWithTransfer();
    } catch (error) {
      this.toastService.showError('Failed to check active plans/packs. Please try again.');
      console.error('Error checking active plans/packs:', error);
    }
  }


  private async proceedWithTransfer() {
    this.showTransferMemberModal = true;
    const gridReq: PartnerGridRequest = {
      dateFilters: undefined,
      export: true,
      filters: undefined,
      page: 1,
      pageSize: 100,
      searchQuery: undefined,
      sort: undefined,
    };
    const girdRes = await lastValueFrom(
      this.partnerService.partnerList(gridReq),
    );
    const partners = girdRes.data;
    this.partnerList = partners
      .map((value) => {
        return {
          label: `${value.partner} (${value.partnerId})`,
          value: value.partnerId,
        };
      })
      .filter((value) => value.value != 'PR-9008');
  }

  handleCancelTransferModal() {
    this.selectedMemberId = undefined;
    this.showTransferMemberModal = false;
  }

  handleOkTransferModal() {
    if (!this.selectedPartner) {
      this.toastService.showError('Please select a partner');
      return;
    }
    if (!this.selectedMemberId) {
      this.toastService.showError('Please select a Member');
      return;
    }
    this.transferLoading = true;
    this.partnerService
      .transferMember(this.selectedPartner, this.selectedMemberId!)
      .subscribe({
        next: async (value) => {
          this.toastService.showSuccess('Member transferred successfully');
          this.showTransferMemberModal = false;
          this.reloadTable.emit();
          this.transferLoading = false;
        },
        error: (error) => {
          this.transferLoading = false;
          const errorBody = error.error as IAnyoError;
          this.toastService.showAnyoErrorToast(errorBody.description);
        },
      });
  }

  async manageMember(id: string, partnerName: string) {
    await this.router.navigate([
      '/partner/addmember/' + id + '/' + partnerName,
    ]);
  }

  viewUserProfileRole = false;

  ngOnInit() {
    this.auth.currentAuthStatus.subscribe((user) => {
      if (user) {
        if (user.customClaims?.roles.includes(Roles.ViewUserProfile)) {
          this.viewUserProfileRole = true;
        }
      }
    });
  }

  /**
   * Check if a member has active therapy plans or packs
   * @param memberId - The member ID to check
   * @returns Promise with counts of active plans and packs
   */
  private async checkActivePlansAndPacks(memberId: string): Promise<{
    activePlans: number;
    activePacks: number;
  }> {
    try {
      const response = await lastValueFrom(
        this.memberService.checkActiveSubscriptions(memberId)
      );

      return {
        activePlans: response.activePlansCount,
        activePacks: response.activePacksCount,
      };
    } catch (error) {
      return {
        activePlans: 0,
        activePacks: 0,
      };
    }
  }


  private showActivePlansPacksWarning() {
    this.showActivePlansWarningModal = true;
  }

  handleProceedWithActivePlans() {
    this.showActivePlansWarningModal = false;

    if (this.pendingAction === 'transfer') {
      this.proceedWithTransfer();
    } else if (this.pendingAction === 'delete') {
      this.proceedWithDelete();
    } else if (this.pendingAction === 'bulk-delete') {
      this.proceedWithBulkDelete();
    } else if (this.pendingAction === 'convert') {
      this.proceedWithConvert();
    }

    this.pendingAction = null;
    this.activePlansCount = 0;
    this.activePacksCount = 0;
  }

  handleCancelActivePlansWarning() {
    this.showActivePlansWarningModal = false;
    this.pendingAction = null;
    this.selectedMemberId = undefined;
    this.selectedMemberEmail = undefined;
    this.activePlansCount = 0;
    this.activePacksCount = 0;
    this.membersWithActivePlans = [];
  }


  private proceedWithDelete() {
    if (!this.selectedMemberId) return;

    this.modal.confirm({
      nzTitle: 'Delete Member',
      nzContent: 'Are you sure you want to delete this member?',
      nzOnOk: async () => {
        try {
          await lastValueFrom(this.memberService.deleteMember(this.selectedMemberId!));
          this.toastService.showSuccess('Member deleted successfully');
          this.reloadTable.emit();
        } catch (err) {
          const errorBody = err as { error: IAnyoError };
          this.toastService.showAnyoErrorToast(errorBody.error.description);
        }
      },
      nzOkText: 'Delete',
      nzCancelText: 'Cancel',
      nzClosable: false,
    });
  }

  removeFamilyMemberAlert(memberId: string, hasAttendedSession: boolean) {
    this.selectedMemberId = memberId;
    const nzContent = hasAttendedSession
      ? 'This member has already used a paid benefit on this pass. You can still remove them, but this pass will be locked — User won\'t be able to invite someone new in their place.'
      : 'This member hasn\'t used any paid benefits yet. They will lose access to therapy, expert consults and paid events, and become a regular retail member.';
    this.modal.confirm({
      nzTitle: 'Remove Family Member',
      nzContent,
      nzOnOk: async () => {
        try {
          await lastValueFrom(
            this.memberService.removeFamilyMember(
              this.selectedMemberId!,
              hasAttendedSession,
            ),
          );
          this.toastService.showSuccess(
            'Family member removed and converted to retail successfully',
          );
          this.reloadTable.emit();
        } catch (err) {
          const errorBody = err as { error: { error: string; description: string } };
          if (errorBody.error.error === 'FAMILY_MEMBER_HAS_SESSIONS') {
            this.toastService.showError(
              'Cannot remove — member has active or completed sessions.',
            );
          } else {
            this.toastService.showAnyoErrorToast(errorBody.error.description);
          }
        }
      },
      nzOnCancel: () => {
        this.selectedMemberId = undefined;
      },
      nzOkText: 'Remove',
      nzCancelText: 'Cancel',
      nzClosable: false,
    });
  }

  private proceedWithBulkDelete() {
    this.modal.confirm({
      nzTitle: 'Delete Members',
      nzContent: `Do you want to delete ${this.setOfCheckedId.size} members?`,
      nzOnOk: async () => {
        try {
          const deletionPromises = Array.from(this.setOfCheckedId).map((id) =>
            lastValueFrom(this.memberService.deleteMember(id))
          );
          await Promise.all(deletionPromises);
          this.toastService.showSuccess('All members deleted successfully');
          this.reloadTable.emit();
        } catch (err) {
          const errorBody = err as { error: IAnyoError };
          this.toastService.showAnyoErrorToast(errorBody.error.description);
        }
      },
      nzOkText: 'Delete',
      nzCancelText: 'Cancel',
      nzClosable: false,
    });
  }

  private proceedWithConvert() {
    if (!this.selectedMemberEmail) return;
    this.convertCorporateUserToRetailFilterEvent.emit(this.selectedMemberEmail);
  }

}
